import {
  inventoryApi,
  suggestionApi,
  SuggestionApiSuggestionTypes,
} from '@ardoq/api';
import { ArdoqId } from '@ardoq/api-types';
import { isArdoqError } from '@ardoq/common-helpers';
import { IconName } from '@ardoq/icons';
import { LoadComponentSuggestionsForReferenceCreation } from './componentSuggestionTypes';
import { logError } from '@ardoq/logging';

export type LabelAndValue = {
  label: string;
  value: string;
  description?: string;
  breadcrumbs?: Array<{ text: string; icon?: IconName }>;
};

export type FetchSuggestionFn = (name: string) => Promise<Array<LabelAndValue>>;

export type GetSuggestionsParams = {
  readonly workspaceIds?: ArdoqId[] | null;
  readonly componentId?: ArdoqId;
};

const mapString = (result: string) => ({ value: result, label: result });

const getSuggestionsFetch =
  <T>(
    urlSegment: SuggestionApiSuggestionTypes,
    mapFn: (result: T) => LabelAndValue,
    workspaceIds?: ArdoqId[] | null,
    componentId?: ArdoqId
  ): FetchSuggestionFn =>
  async (name: string) => {
    const results = await suggestionApi.getQuery<T>(
      urlSegment,
      name,
      workspaceIds,
      componentId
    );
    if (isArdoqError(results)) {
      logError(results, 'Loading search suggestions failed');
      return [];
    }
    return results.map(mapFn);
  };

export const getLoadComponentSuggestionsAsync = (
  params?: GetSuggestionsParams
) =>
  getSuggestionsFetch(
    'component',
    ({
      name,
      id,
      workspaceName,
    }: {
      name: string;
      id: string;
      workspaceName?: string;
    }) => ({
      label: name,
      value: id,
      breadcrumbs: workspaceName
        ? [{ text: workspaceName, icon: IconName.WORKSPACE }]
        : undefined,
    }),
    params?.workspaceIds
  );

export const getLoadParentSuggestionsAsync = (params?: GetSuggestionsParams) =>
  getSuggestionsFetch(
    'parent',
    ({
      name,
      id,
      workspaceName,
    }: {
      name: string;
      id: string;
      workspaceName?: string;
    }) => ({
      label: name,
      value: id,
      breadcrumbs: workspaceName
        ? [{ text: workspaceName, icon: IconName.WORKSPACE }]
        : undefined,
    }),
    params?.workspaceIds,
    params?.componentId
  );

export const getLoadWorkspaceSuggestionsAsync = (
  params?: GetSuggestionsParams
) =>
  getSuggestionsFetch(
    'workspace',
    ({ name, id }: { name: string; id: string }) => ({
      label: name,
      value: id,
    }),
    params?.workspaceIds
  );

export const getLoadUserSuggestionsAsync = (params?: GetSuggestionsParams) =>
  getSuggestionsFetch(
    'users',
    ({ name, id, email }: { name: string; id: string; email: string }) => ({
      label: name,
      description: email,
      value: id,
    }),
    params?.workspaceIds
  );

export const getLoadTagSuggestionsAsync = (params?: GetSuggestionsParams) =>
  getSuggestionsFetch('tag', mapString, params?.workspaceIds);

export const getLoadComponentTypeNameSuggestionsAsync = (
  params?: GetSuggestionsParams
) => getSuggestionsFetch('componentName', mapString, params?.workspaceIds);

export const getLoadReferenceTypeNameSuggestionsAsync = (
  params?: GetSuggestionsParams
) => getSuggestionsFetch('referenceName', mapString, params?.workspaceIds);

/**
 * When we load source or target component suggestions to create references of a certain reference
 * type, we use the component type as a constraint to limit the set of possible suggestions.
 * On top of that, we pass `referenceTypeName` for source component suggestions
 * (`direction=incoming`), so that we filter out components form workspaces that don't have such
 * reference type defined (to avoid suggesting components that cannot use this reference).
 */
export const getLoadComponentSuggestionsAsyncForReferenceCreation =
  (
    componentTypeName: string,
    direction: 'incoming' | 'outgoing',
    referenceTypeName?: string
  ): LoadComponentSuggestionsForReferenceCreation =>
  async searchString => {
    const result =
      await inventoryApi.getComponentSuggestionsForReferenceCreation({
        componentTypeName,
        searchString,
        direction,
        referenceTypeName,
      });

    if (isArdoqError(result)) {
      logError(result, 'Loading component suggestions failed');
      return [];
    }
    return result.map(
      ({ name, id, workspaceName, style, referenceTypeId }) => ({
        style,
        label: name,
        value: id,
        referenceTypeId,
        breadcrumbs: workspaceName
          ? [{ text: workspaceName, icon: IconName.WORKSPACE }]
          : undefined,
      })
    );
  };
